import { createPortal } from 'react-dom';
import { X, FileText, Download, History, ChevronLeft, ChevronRight, Loader2 } from 'lucide-react';

import { useState, useMemo } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { formatCNPJ } from '@/lib/utils/formatters';
import { useToast } from '@/hooks/use-toast';
import { useGerarLinkTemporario } from '@/services/General';
import { useListarHistoricoSolicitacoes } from '@/services/Relatorios';

const ITENS_POR_PAGINA = 5;

interface ModalHistoricoSolicitacoesProps {
  aberto: boolean;
  onFechar: () => void;
}

export function ModalHistoricoSolicitacoes({ aberto, onFechar }: ModalHistoricoSolicitacoesProps) {
  const [paginaAtual, setPaginaAtual] = useState(1);
  const [downloadingId, setDownloadingId] = useState<number | null>(null);
  const { toast } = useToast();
  const { data, isLoading } = useListarHistoricoSolicitacoes({ enabled: aberto });
  const gerarLinkTemporario = useGerarLinkTemporario();

  const solicitacoes = useMemo(() => data?.data ?? [], [data?.data]);
  const totalItens = data?.totalItens ?? 0;
  const totalPaginas = Math.ceil(totalItens / ITENS_POR_PAGINA);

  const solicitacoesPaginadas = useMemo(() => {
    const inicio = (paginaAtual - 1) * ITENS_POR_PAGINA;
    return solicitacoes.slice(inicio, inicio + ITENS_POR_PAGINA);
  }, [paginaAtual, solicitacoes]);

  const extrairNomeArquivo = (caminho: string) => caminho.split('/').pop() ?? caminho;

  const handleDownload = async (caminho: string, id: number) => {
    setDownloadingId(id);

    try {
      if (!caminho) {
        throw new Error('Caminho do arquivo inválido.');
      }

      const linkResponse = await gerarLinkTemporario.mutateAsync({
        Funcao: 'GET',
        Bucket: 'imposto-cache',
        Key: caminho,
      });

      if (!linkResponse.status?.resultado) {
        throw new Error(
          linkResponse.status?.mensagem || 'Não foi possível gerar o link de download.'
        );
      }

      const urlDownload =
        typeof linkResponse.data === 'string' ? linkResponse.data : linkResponse.data?.url;

      if (!urlDownload) {
        throw new Error('Link de download não retornado pela API.');
      }

      const a = document.createElement('a');
      a.href = urlDownload;
      a.target = '_blank';
      a.rel = 'noreferrer';
      a.download = extrairNomeArquivo(caminho);
      document.body.appendChild(a);
      a.click();
      a.remove();

      toast({
        title: 'Download iniciado',
        description: 'O arquivo será aberto em uma nova janela.',
      });
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Não foi possível baixar o arquivo.';
      toast({
        variant: 'destructive',
        title: 'Erro ao fazer download',
        description: message,
      });
    } finally {
      setDownloadingId(null);
    }
  };

  if (!aberto) return null;

  const content = (
    <>
      <div className="fixed inset-0 bg-black/50 z-100" onClick={onFechar} />
      <div className="fixed inset-0 flex items-end sm:items-center justify-center z-100 p-0 sm:p-4">
        <div
          className="bg-card border-t sm:border border-border rounded-t-2xl sm:rounded-xl w-full sm:max-w-4xl h-[90vh] sm:h-auto sm:max-h-[90vh] flex flex-col shadow-xl animate-in slide-in-from-bottom-full sm:slide-in-from-bottom-0 duration-300"
          onClick={e => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between px-4 sm:px-6 py-4 border-b border-border shrink-0">
            <div className="min-w-0 flex-1 pr-2">
              <div className="flex items-start gap-2 sm:gap-3">
                <div className="shrink-0 w-8 h-8 sm:w-9 sm:h-9 rounded-full bg-primary/10 flex items-center justify-center">
                  <History className="h-4 w-4 sm:h-5 sm:w-5 text-primary" />
                </div>
                <div className="min-w-0">
                  <h2 className="text-base sm:text-lg font-semibold truncate">
                    Histórico de Solicitações
                  </h2>
                  <p className="text-xs sm:text-sm text-muted-foreground mt-0.5">
                    {totalItens} solicitaç{totalItens === 1 ? 'ão' : 'ões'} encontrada
                    {totalItens === 1 ? '' : 's'}
                  </p>
                </div>
              </div>
            </div>
            <Button
              variant="ghost"
              size="icon"
              onClick={onFechar}
              className="rounded-full shrink-0 h-9 w-9"
            >
              <X className="h-5 w-5" />
            </Button>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto px-3 sm:px-6 py-4 overscroll-contain">
            {isLoading ? (
              <div className="flex flex-col items-center justify-center py-12 space-y-3">
                <Loader2 className="h-6 w-6 animate-spin text-primary" />
                <p className="text-sm text-muted-foreground">Carregando histórico...</p>
              </div>
            ) : (
              <div className="space-y-3">
                {solicitacoesPaginadas.map(solicitacao => {
                  const nomeArquivo = extrairNomeArquivo(solicitacao.Caminho_Relatorio);

                  return (
                    <div
                      key={solicitacao.ID}
                      className="bg-muted/30 border border-border rounded-lg p-3 sm:p-4 hover:bg-muted/50 transition-colors"
                    >
                      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                        <div className="flex-1 min-w-0 space-y-3">
                          <div className="flex items-start gap-2 flex-wrap">
                            <Badge
                              variant={solicitacao.Tipo === 'PDF' ? 'default' : 'secondary'}
                              className="text-xs shrink-0"
                            >
                              <FileText className="h-3 w-3 mr-1" />
                              {solicitacao.Tipo}
                            </Badge>
                            <span
                              className="font-semibold text-xs sm:text-sm break-all line-clamp-2"
                              title={nomeArquivo}
                            >
                              {nomeArquivo}
                            </span>
                          </div>

                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                            <div className="space-y-0.5">
                              <span className="text-muted-foreground text-xs block">
                                CNPJ Gestor
                              </span>
                              <p className="font-medium text-xs sm:text-sm break-all">
                                {formatCNPJ(solicitacao.CNPJ_Gestor)}
                              </p>
                            </div>
                            <div className="space-y-0.5">
                              <span className="text-muted-foreground text-xs block">Data/Hora</span>
                              <p className="font-medium text-xs sm:text-sm">
                                {new Date(solicitacao.DataHoraCriacao).toLocaleString('pt-BR', {
                                  day: '2-digit',
                                  month: '2-digit',
                                  year: 'numeric',
                                  hour: '2-digit',
                                  minute: '2-digit',
                                })}
                              </p>
                            </div>
                            <div className="space-y-0.5">
                              <span className="text-muted-foreground text-xs block">Usuário</span>
                              <p className="font-medium text-xs sm:text-sm break-all">
                                {solicitacao.UsuarioSolicitacao}
                              </p>
                            </div>
                            <div className="space-y-0.5">
                              <span className="text-muted-foreground text-xs block">ID</span>
                              <p className="font-medium text-xs sm:text-sm">{solicitacao.ID}</p>
                            </div>
                          </div>
                        </div>

                        <div className="shrink-0 w-full sm:w-auto">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() =>
                              handleDownload(solicitacao.Caminho_Relatorio, solicitacao.ID)
                            }
                            disabled={downloadingId === solicitacao.ID}
                            className="w-full sm:w-auto h-9"
                          >
                            {downloadingId === solicitacao.ID ? (
                              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                            ) : (
                              <Download className="h-4 w-4 mr-2" />
                            )}
                            <span className="text-xs sm:text-sm">Baixar</span>
                          </Button>
                        </div>
                      </div>
                    </div>
                  );
                })}
                {solicitacoesPaginadas.length === 0 && (
                  <div className="flex flex-col items-center justify-center py-12 space-y-3">
                    <History className="h-6 w-6 text-muted-foreground" />
                    <p className="text-sm text-muted-foreground">Nenhuma solicitação encontrada.</p>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Footer com paginação */}
          {totalPaginas > 1 && (
            <div className="flex items-center justify-between px-4 sm:px-6 py-3 border-t border-border shrink-0">
              <span className="text-xs text-muted-foreground">
                Página {paginaAtual} de {totalPaginas}
              </span>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="icon"
                  className="h-8 w-8"
                  onClick={() => setPaginaAtual(p => Math.max(1, p - 1))}
                  disabled={paginaAtual === 1}
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <Button
                  variant="outline"
                  size="icon"
                  className="h-8 w-8"
                  onClick={() => setPaginaAtual(p => Math.min(totalPaginas, p + 1))}
                  disabled={paginaAtual === totalPaginas}
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );

  return createPortal(content, document.body);
}
